import { useEffect, useState } from 'react';

import classNames from 'classnames';
import _ from 'lodash';
import { DateTime } from 'luxon';
import FileTypeIcon from 'pages/MyFiles/FileTypeIcon';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { ReactComponent as ArrowRightIcon } from 'assets/icons/arrow-right-new.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/trash2.svg';
import useConfirmDialog from 'components/common/ConfirmationModal/useConfirmDialog';
import LoaderWithFade from 'components/common/LoaderWithFade/LoaderWithFade';
import NothingFound from 'components/common/NothingFound/NothingFound';
import OptionButton from 'components/common/OptionButton/OptionButton';
import useSelectedIds from 'hooks/generic/useSelectedIds';
import requestAPI from 'utils/apiHandler';

import styles from './RequestLists.module.scss';
import { AiReview } from './types';

const RequetsLists = () => {
  const [items, setItems] = useState<AiReview[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState<'name' | 'created'>('created');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const { ids, addId, removeId } = useSelectedIds([]);
  const { deleteConfirmation } = useConfirmDialog();
  const router = useHistory();
  const { t } = useTranslation();

  const loadItems = () => {
    setIsLoading(true);
    requestAPI('review')
      .then(request => setItems(request))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => loadItems(), []);

  const deleteReview = async (id: string) => {
    setIsLoading(true);
    await requestAPI({
      url: `/review/${id}`,
      method: 'DELETE',
    });
    setItems(val => val.filter(i => i._id !== id));
    setIsLoading(false);
  };

  const itemsSorted = _.orderBy(
    items,
    [
      item => {
        if (sortColumn === 'name') {
          return `${item.subject.title} ${item.class.title}`.toLowerCase();
        }
        if (sortColumn === 'created') {
          return DateTime.fromISO(item.created_at).toUnixInteger();
        }
      },
    ],
    [sortOrder]
  );

  const getHeaderCell = (key: 'name' | 'created', title: string) => {
    return (
      <th className="text-label">
        <div
          className={styles.thContainer}
          onClick={() => {
            setSortColumn(key);
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
          }}
        >
          {title}
          <div className={styles.arrowIconContainer}>
            <ArrowRightIcon
              className={styles.arrowDownIcon}
              style={{ rotate: sortOrder === 'asc' ? '90deg' : '270deg' }}
            />
          </div>
        </div>
      </th>
    );
  };

  return (
    <div>
      {itemsSorted.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr>
              {getHeaderCell('name', t('common:name'))}
              {getHeaderCell('created', t('common:modified'))}
            </tr>
          </thead>
          <tbody>
            {itemsSorted.map(item => [
              <tr
                key={item._id}
                className={classNames({
                  [styles.itemSelected]: ids.includes(item._id),
                })}
                onClick={() => router.push(`/ai/review/${item._id}`)}
              >
                <td>
                  <div style={{ display: 'flex' }}>
                    <div className={styles.iconContainer}>
                      <FileTypeIcon type="material" />
                    </div>
                    <div className={styles.title}>
                      {item.subject.title} - {item.class.title}
                    </div>
                  </div>
                </td>
                <td className={styles.text} style={{ position: 'relative' }}>
                  {window.formatDateTime(item.created_at, 'DATETIME_SHORT')}
                  <div
                    className={styles.optionButtonStyles}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <OptionButton
                      hoverCircleVariant={'secondary'}
                      dotsVariant={'secondary'}
                      onOptionsVisibleChange={isVisible => {
                        isVisible ? addId(item._id) : removeId(item._id);
                      }}
                      options={[
                        {
                          id: 'delete',
                          label: t('common:delete'),
                          icon: <TrashIcon />,
                          danger: true,
                        },
                      ]}
                      onClick={async id => {
                        if (id === 'delete') {
                          if (
                            await deleteConfirmation({
                              title: t('common:delete'),
                              message: t('aireview:delete_review_confirmation'),
                            })
                          ) {
                            deleteReview(item._id);
                          }
                        }
                      }}
                    />
                  </div>
                </td>
              </tr>,
              <tr key={`${item._id}_`} className={styles.spacerRow}>
                <td className={styles.spacerCell} colSpan={99}></td>
              </tr>,
            ])}
          </tbody>
        </table>
      )}
      {items.length === 0 && !isLoading && <NothingFound label={t('aireview:AI_Document_Review')} />}
      {isLoading && (
        <center>
          <LoaderWithFade />
        </center>
      )}
    </div>
  );
};

export default RequetsLists;
